import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import CallFlowNode from '../components/callflow/CallFlowNode';
import ReplayPlayer from '../components/callflow/ReplayPlayer';
import { decorateEdges } from '../components/callflow/edgeDecorator';
import '../components/callflow/flowStyles';
import { useTheme } from '../context/ThemeContext';

/* ─────────────────────────────────────────────────────────────────
   Public, read-only replay page (`/replay/:token`).
   No auth — token-guarded server-side. Renders the canvas with the
   execution's path automatically loaded into ReplayPlayer.
   ───────────────────────────────────────────────────────────────── */

const nodeTypes = {
  start: CallFlowNode, menu: CallFlowNode, time_router: CallFlowNode,
  tag_filter: CallFlowNode, contact_filter: CallFlowNode,
  ai_agent: CallFlowNode, transfer: CallFlowNode, hangup: CallFlowNode,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function PublicReplayInner() {
  const { token } = useParams();
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? 'dark' : 'light';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/public/replay/${token}`);
        setData(r.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This replay link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const flow = data?.flow;
  const execution = data?.execution;

  const replaySteps = useMemo(
    () => (execution?.steps || []).filter((s) => s.node_id),
    [execution]
  );

  const pulsedNodeIds = useMemo(() => {
    if (!execution) return {};
    const out = {};
    replaySteps.forEach((s, i) => {
      if (i <= stepIndex) {
        out[s.node_id] = { status: i === stepIndex ? s.status : 'visited' };
      }
    });
    return out;
  }, [execution, replaySteps, stepIndex]);

  const decoratedNodes = useMemo(() => {
    if (!flow?.nodes) return [];
    return flow.nodes.map((n) => {
      const data = { ...(n.data || {}) };
      if (n.type === 'start') data._phoneNumber = flow.phone_number || '';
      const p = pulsedNodeIds[n.id];
      if (p) data._pulse = p.status;
      return { ...n, data };
    });
  }, [flow, pulsedNodeIds]);

  const decoratedEdges = useMemo(
    () => decorateEdges(flow?.edges || [], flow?.nodes || [], { readOnly: true }),
    [flow]
  );

  const togglePlay = useCallback(() => {
    if (stepIndex >= replaySteps.length - 1 && !playing) {
      setStepIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  }, [stepIndex, replaySteps.length, playing]);

  const stepForward = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, Math.max(0, replaySteps.length - 1)));
    if (stepIndex + 1 >= replaySteps.length - 1) setPlaying(false);
  }, [replaySteps.length, stepIndex]);

  const stepBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
    setPlaying(false);
  }, []);

  const seek = useCallback((idx) => { setStepIndex(idx); setPlaying(false); }, []);

  // Loading / error states
  if (loading) {
    return (
      <div
        data-vox-theme={theme}
        className="h-screen w-screen flex items-center justify-center"
        style={{ background: 'var(--vox-canvas-bg)' }}
        data-testid="public-replay-loading"
      >
        <Loader2 size={28} className="text-teal-400 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-vox-theme={theme}
        className="h-screen w-screen flex items-center justify-center p-8"
        style={{ background: 'var(--vox-canvas-bg)' }}
        data-testid="public-replay-error"
      >
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/15 border border-rose-400/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={22} className="text-rose-300" />
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--vox-text-primary)' }}>Replay unavailable</h1>
          <p className="text-sm" style={{ color: 'var(--vox-text-secondary)' }}>{error}</p>
          <a href="https://voxinity.ai" className="inline-flex items-center gap-1.5 mt-6 text-xs text-teal-300 hover:text-teal-200">
            voxinity.ai <ExternalLink size={11} />
          </a>
        </div>
      </div>
    );
  }

  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso || ''; }
  };

  return (
    <div
      data-vox-theme={theme}
      className="h-screen w-screen flex flex-col overflow-hidden"
      style={{ background: 'var(--vox-canvas-bg)' }}
      data-testid="public-replay-page"
    >
      {/* Header */}
      <header
        className="flex-shrink-0 h-14 px-5 flex items-center gap-3 border-b"
        style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }}
      >
        <div className="w-8 h-8 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center">
          <img src="/voxinity-mark.svg" alt="" className="w-4 h-4" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: 'var(--vox-text-primary)' }} data-testid="public-replay-flow-name">
            {flow?.name} — Call Replay
          </div>
          <div className="text-[10px] flex items-center gap-2" style={{ color: 'var(--vox-text-muted)' }}>
            <span className="font-mono">{execution?.from_number || 'Unknown'}</span>
            <span style={{ opacity: 0.5 }}>→</span>
            <span className="font-mono">{flow?.phone_number}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{fmtTs(execution?.started_at)}</span>
            {execution?.was_live_test && <span className="text-amber-500 font-bold ml-1">LIVE TEST</span>}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vox-text-muted)' }}>Read-only · shared via Voxinity AI</span>
      </header>

      {/* Canvas */}
      <div className="flex-1 relative" data-testid="public-replay-canvas">
        <ReactFlow
          className="voxinity-flow"
          nodes={decoratedNodes}
          edges={decoratedEdges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
          defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
          defaultViewport={flow?.canvas_viewport || { x: 80, y: 80, zoom: 0.85 }}
          fitView={!(flow?.canvas_viewport)}
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="rgba(255,255,255,0.06)" gap={20} size={1} />
          <Controls position="bottom-left" showInteractive={false} />
          <MiniMap pannable zoomable nodeStrokeWidth={3} nodeColor={(n) => {
            const colors = { start: '#34d399', menu: '#38bdf8', time_router: '#38bdf8',
              tag_filter: '#38bdf8', contact_filter: '#38bdf8', ai_agent: '#2dd4bf',
              transfer: '#2dd4bf', hangup: '#fb7185' };
            return colors[n.type] || '#94a3b8';
          }} />
        </ReactFlow>

        <AnimatePresence>
          <ReplayPlayer
            key="player"
            execution={execution}
            stepIndex={stepIndex}
            playing={playing}
            speed={speed}
            onTogglePlay={togglePlay}
            onStepBack={stepBack}
            onStepForward={stepForward}
            onSeek={seek}
            onSpeedChange={setSpeed}
            onClose={() => { setPlaying(false); setStepIndex(0); }}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function PublicReplayPage() {
  return (
    <ReactFlowProvider>
      <PublicReplayInner />
    </ReactFlowProvider>
  );
}
